import { throwable } from "../types/index.js";
import { warn } from "../warn.js";
import { types } from "./types.js";
const createRoute = (creatorOptions) => (path, types = {}, children) => {
    return Object.assign(Object.assign(Object.assign({}, decorateChildren(path, types, creatorOptions, children, false)), getRoute(path, types, creatorOptions)), { $: decorateChildren(path, types, creatorOptions, children, true) });
};
function decorateChildren(path, typesObj, creatorOptions, children, excludePath) {
    const result = {};
    if (children) {
        Object.keys(children).forEach((key) => {
            const value = children[key];
            result[key] = isRoute(value)
                ? Object.assign(Object.assign(Object.assign({}, decorateChildren(path, typesObj, creatorOptions, value, excludePath)), getRoute(excludePath || path === ""
                    ? value.path.substring(1)
                    : value.path === "/"
                        ? path
                        : `${path}${value.path}`, types(excludePath ? Object.assign(Object.assign({}, typesObj), { params: undefined }) : typesObj)(value.types), creatorOptions)), { $: decorateChildren(path, typesObj, creatorOptions, value.$, true) }) : value;
        });
    }
    return result;
}
function getRoute(path, types, creatorOptions) {
    const keys = getKeys(path);
    const relativePath = removeIntermediateStars(path);
    function getPlainParams(params) {
        return getPlainParamsByTypes(keys, params, types.params);
    }
    function getPlainSearchParams(params) {
        return getPlainSearchParamsByTypes(params, types.searchParams);
    }
    function buildRelativePathname(params) {
        const rawBuiltPath = creatorOptions.generatePath(relativePath, getPlainParams(params));
        return rawBuiltPath.startsWith("/") ? rawBuiltPath.substring(1) : rawBuiltPath;
    }
    function buildSearch(params) {
        const searchString = creatorOptions.createSearchParams(getPlainSearchParams(params)).toString();
        return searchString ? `?${searchString}` : "";
    }
    function buildHash(hash) {
        return `#${hash}`;
    }
    function buildState(params) {
        return getPlainStateParamsByTypes(params, types.state);
    }
    function buildRelativePath(params, searchParams, hash) {
        return `${buildRelativePathname(params)}${searchParams !== undefined ? buildSearch(searchParams) : ""}${hash !== undefined ? buildHash(hash) : ""}`;
    }
    function buildRelativeUrl(params, searchParams, hash) {
        warn("buildRelativeUrl is deprecated, use buildRelativePath instead.");
        return buildRelativePath(params, searchParams, hash);
    }
    function buildPath(params, searchParams, hash) {
        return `/${buildRelativePath(params, searchParams, hash)}`;
    }
    function buildUrl(params, searchParams, hash) {
        warn("buildUrl is deprecated, use buildPath instead.");
        return buildPath(params, searchParams, hash);
    }
    function getTypedParams(params) {
        return getTypedParamsByTypes(keys, params, types.params);
    }
    function getUntypedParams(params) {
        const result = {};
        const typedKeys = keys[0];
        Object.keys(params).forEach((key) => {
            if (typedKeys.indexOf(key) === -1) {
                result[key] = params[key];
            }
        });
        return result;
    }
    function getTypedSearchParams(params) {
        return getTypedSearchParamsByTypes(params, types.searchParams);
    }
    function getUntypedSearchParams(params) {
        const result = creatorOptions.createSearchParams(params);
        if (!types.searchParams)
            return result;
        Object.keys(types.searchParams).forEach((key) => {
            result.delete(key);
        });
        return result;
    }
    function getTypedState(state) {
        return getTypedStateByTypes(state, types.state);
    }
    function getUntypedState(state) {
        const result = {};
        if (!isRecord(state))
            return result;
        const typedKeys = types.state ? Object.keys(types.state) : [];
        Object.keys(state).forEach((key) => {
            if (typedKeys.indexOf(key) === -1) {
                result[key] = state[key];
            }
        });
        return result;
    }
    function getTypedHash(hash) {
        return getTypedHashByValues(hash, types.hash);
    }
    return {
        path: `/${path}`,
        relativePath,
        buildPath,
        buildRelativePath,
        buildSearch,
        buildHash,
        buildState,
        getTypedParams,
        getTypedSearchParams,
        getTypedHash,
        getTypedState,
        getUntypedParams,
        getUntypedSearchParams,
        getUntypedState,
        getPlainParams,
        getPlainSearchParams,
        types: types,
        buildUrl,
        buildRelativeUrl,
    };
}
function getPlainParamsByTypes(keys, params, types) {
    const result = {};
    Object.keys(params).forEach((key) => {
        const type = types === null || types === void 0 ? void 0 : types[key];
        const value = params[key];
        if (type && keys[0].indexOf(key) !== -1 && value !== undefined) {
            if (isParamType(type)) {
                result[key] = type.getPlainParam(value);
            }
            else {
                result[key] = type.getPlain(value);
            }
        }
        else if (typeof value === "string") {
            result[key] = value;
        }
    });
    return result;
}
function getPlainSearchParamsByTypes(params, types) {
    const result = {};
    Object.keys(params).forEach((key) => {
        const type = types === null || types === void 0 ? void 0 : types[key];
        if (type && params[key] !== undefined) {
            if (isSearchParamType(type)) {
                result[key] = type.getPlainSearchParam(params[key]);
            }
            else {
                result[key] = type.getPlain(params[key]);
            }
        }
    });
    return result;
}
function getPlainStateParamsByTypes(params, types) {
    const result = {};
    Object.keys(params).forEach((key) => {
        const type = types === null || types === void 0 ? void 0 : types[key];
        const value = params[key];
        if (type && value !== undefined) {
            if (isStateParamType(type)) {
                result[key] = type.getPlainStateParam(value);
            }
            else {
                result[key] = type.getPlain(value);
            }
        }
    });
    return result;
}
function getTypedParamsByTypes(keys, pathParams, types) {
    const result = {};
    keys[0].forEach((key) => {
        const type = types === null || types === void 0 ? void 0 : types[key];
        if (type) {
            if (isParamType(type)) {
                result[key] = type.getTypedParam(pathParams[key]);
            }
            else {
                try {
                    result[key] = type.getTyped(pathParams[key]);
                }
                catch (error) {
                    if (isThrowableError(error)) {
                        throw error[0];
                    }
                    result[key] = undefined;
                }
            }
        }
        else {
            if (typeof pathParams[key] === "string") {
                result[key] = pathParams[key];
            }
            else {
                if (keys[1].indexOf(key) === -1) {
                    throw new Error(`Expected param ${key} to exist in the given path. Most likely you're rendering the component at a wrong path. You can make it optional or explicitly specify its type as string().`);
                }
            }
        }
    });
    return result;
}
function getTypedSearchParamsByTypes(searchParams, types) {
    const result = {};
    if (types) {
        Object.keys(types).forEach((key) => {
            const type = types[key];
            if (type) {
                if (isSearchParamType(type)) {
                    result[key] = type.getTypedSearchParam(searchParams.getAll(key));
                }
                else {
                    try {
                        result[key] = type.getTyped(type.isArray ? searchParams.getAll(key) : searchParams.get(key));
                    }
                    catch (error) {
                        if (isThrowableError(error)) {
                            throw error[0];
                        }
                        result[key] = undefined;
                    }
                }
            }
        });
    }
    return result;
}
function getTypedHashByValues(hash, hashValues) {
    const normalizedHash = hash === null || hash === void 0 ? void 0 : hash.substring(1, hash === null || hash === void 0 ? void 0 : hash.length);
    if ((hashValues === null || hashValues === void 0 ? void 0 : hashValues.length) === 0 || (normalizedHash && hashValues && hashValues.indexOf(normalizedHash) !== -1)) {
        return normalizedHash;
    }
    return undefined;
}
function getTypedStateByTypes(state, types) {
    const result = {};
    if (types && isRecord(state)) {
        Object.keys(types).forEach((key) => {
            const type = types[key];
            if (type) {
                if (isStateParamType(type)) {
                    result[key] = type.getTypedStateParam(state[key]);
                }
                else {
                    try {
                        result[key] = type.getTyped(state[key]);
                    }
                    catch (error) {
                        if (isThrowableError(error)) {
                            throw error[0];
                        }
                        result[key] = undefined;
                    }
                }
            }
        });
    }
    return result;
}
function getKeys(path) {
    const allParams = [];
    const optionalParams = [];
    path.split(":")
        .filter((_, index) => Boolean(index))
        .forEach((part) => {
        const rawParam = part.split("/")[0];
        if (rawParam.endsWith("?")) {
            const param = rawParam.replace("?", "");
            allParams.push(param);
            optionalParams.push(param);
        }
        else {
            allParams.push(rawParam);
        }
    });
    if (path.includes("*?")) {
        allParams.push("*");
        optionalParams.push("*");
    }
    else if (path.includes("*")) {
        allParams.push("*");
    }
    return [allParams, optionalParams];
}
function removeIntermediateStars(path) {
    return path.replace(/\*\??\//g, "");
}
function isRoute(value) {
    return Boolean(value && typeof value === "object" && "types" in value && "path" in value);
}
function isRecord(value) {
    return Boolean(value && typeof value === "object");
}
function isThrowableError(error) {
    return Array.isArray(error) && error[1] === throwable;
}
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function isParamType(type) {
    return Boolean(type && typeof type === "object" && "getTypedParam" in type && "getPlainParam" in type);
}
function isSearchParamType(
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type) {
    return Boolean(type && typeof type === "object" && "getTypedSearchParam" in type && "getPlainSearchParam" in type);
}
function isStateParamType(
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type) {
    return Boolean(type && typeof type === "object" && "getTypedStateParam" in type && "getPlainStateParam" in type);
}
export { createRoute, };
